import React from 'react';

import * as analytics from '@adsk/acs-web-analytics';
import { TOOL_IDS } from '@adsk/alloy-provider';
import { DocumentSpreadsheetIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import { TabTitle } from '@adsk/exo-skeleton-react';

import { ScheduleActivityCustomOptionLabel } from './components';
import ScheduleCustomDataView from './components/CustomDataView/ScheduleCustomDataView';
import FiltersPanel from './components/FiltersPanel';
import ScheduleActivityCard from './components/ScheduleActivityCard';
import ScheduleActivityEntityBlock from './components/ScheduleActivityEntityBlock';
import ScheduleActivityEntityItem from './components/ScheduleActiviyEntityItem';
import ScheduleCustomErrorState from './components/ScheduleCustomErrorState';
import ScheduleNoDataEmptyState from './components/ScheduleNoDataEmptyState';
import ScheduleNoSearchResultsEmptyState from './components/ScheduleNoSearchResultsEmptyState';
import { ScheduleProvider } from './components/ScheduleProvider';
import ScheduleTopBar from './components/ScheduleTopBar/ScheduleTopBar';
import { nestedOptions } from './helpers';
import {
  scheduleRelationshipsMetadata,
  SCHEDULE_ANALYTICS,
} from './helpers/consts';
import { getEntitiesByIds, queryEntities } from './scheduleSdk';

export const scheduleActivityPlugin = {
  metadata: scheduleRelationshipsMetadata,
  renderIcon: DocumentSpreadsheetIcon.renderIcon,
  toolId: TOOL_IDS.SCHEDULE,
  nestedOptions,
  is3LOSupported: true,
  showSearchBox: true,
  canCreate: () => Promise.resolve(false),
  EntityBlock: ScheduleActivityEntityBlock,
  CustomOptionLabel: ScheduleActivityCustomOptionLabel,
  PluralText: ({ lowerCase }: { lowerCase: boolean }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const result = T.translate('SCHEDULE_ACTIVITIES');
    if (lowerCase && typeof result === 'string') {
      return result.toLowerCase();
    }
    return result;
  },
  getEntitiesByIds,
  queryEntities,
  PickerModalTitle: ({ showTabTitle = true }: { showTabTitle?: boolean }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const value = T.translate('ADD_SCHEDULE_ACTIVITY_REFERENCE');

    return (
      <>
        {showTabTitle && <TabTitle value={value as string} />}
        {value}
      </>
    );
  },
  CustomTopBar: ScheduleTopBar,
  AddRelationshipsButtonText: ({ count }: { count: number }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const translateKey = count === 1 ? 'ADD_ACTIVITY' : 'ADD_ACTIVITIES';
    return T.translate(translateKey);
  },
  SelectedCount: ({ count }: { count: number }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return T.translate('ACTIVITIES_SELECTED', { count });
  },
  CustomDataView: ScheduleCustomDataView,
  CustomPickerEmptyState: ScheduleNoDataEmptyState,
  CustomPickerNoSearchResults: ScheduleNoSearchResultsEmptyState,
  FiltersPanel,
  CustomRemoveEntityModalTitle: () => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return T.translate('REMOVE_REFERENCE_CONFIRMATION_TITLE');
  },
  EntityMenuItem: ScheduleActivityEntityItem,
  CustomPickerError: ScheduleCustomErrorState,
  Provider: ScheduleProvider,
  onRemove: (
    relationship: { entities: { type: string }[] },
    entity: { type: string },
    projectId: string,
  ) => {
    const type = relationship.entities
      .map((entity: { type: string }) => entity.type)
      .find(
        (entityType: string) =>
          entityType !== scheduleRelationshipsMetadata.type,
      );
    analytics.track(SCHEDULE_ANALYTICS.DELETE, {
      reference_type: type,
      // eslint-disable-next-line spellcheck/spell-checker
      referancing_tool: scheduleRelationshipsMetadata.type,
      project_id: projectId,
    });
  },
  onAddRelationships: (
    source: { type: string },
    entities: unknown[],
    projectId: string,
  ) => {
    entities.forEach(() => {
      analytics.track(SCHEDULE_ANALYTICS.ADD, {
        reference_type: source.type,
        // eslint-disable-next-line spellcheck/spell-checker
        referancing_tool: scheduleRelationshipsMetadata.type,
        project_id: projectId,
      });
    });
  },
  ActivityCard: ScheduleActivityCard,
};
